/**
 * Content Service - Lambda Version
 * Secure access to DynamoDB for Projects, Certifications, and Blogs
 * No AWS credentials needed!
 */

const DYNAMODB_SERVICE_URL = process.env.REACT_APP_DYNAMODB_SERVICE_URL;

// ==================== PROJECTS ====================

/**
 * Get all projects
 */
export const getProjects = async () => {
    try {
        const response = await fetch(DYNAMODB_SERVICE_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                operation: 'get_projects'
            })
        });

        const data = await response.json();
        
        if (!response.ok) {
            throw new Error(data.error || 'Failed to fetch projects');
        }

        return data.projects || [];
    } catch (error) {
        console.error('Error fetching projects:', error);
        throw error;
    }
};

/**
 * Add a new project
 */
export const addProject = async (projectData) => {
    try {
        const response = await fetch(DYNAMODB_SERVICE_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                operation: 'add_project',
                ...projectData
            })
        });

        const data = await response.json();
        
        if (!response.ok) {
            throw new Error(data.error || 'Failed to add project');
        }

        return data;
    } catch (error) {
        console.error('Error adding project:', error);
        throw error;
    }
};

/**
 * Update a project
 */
export const updateProject = async (projectId, projectData) => {
    try {
        const response = await fetch(DYNAMODB_SERVICE_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                operation: 'update_project',
                id: projectId,
                ...projectData
            })
        });

        const data = await response.json();
        
        if (!response.ok) {
            throw new Error(data.error || 'Failed to update project');
        }

        return data;
    } catch (error) {
        console.error('Error updating project:', error);
        throw error;
    }
};

/**
 * Delete a project
 */
export const deleteProject = async (projectId) => {
    try {
        const response = await fetch(DYNAMODB_SERVICE_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                operation: 'delete_project',
                id: projectId
            })
        });

        const data = await response.json();
        
        if (!response.ok) {
            throw new Error(data.error || 'Failed to delete project');
        }

        return data;
    } catch (error) {
        console.error('Error deleting project:', error);
        throw error;
    }
};

// ==================== CERTIFICATIONS ====================

/**
 * Get all certifications
 */
export const getCertifications = async () => {
    try {
        const response = await fetch(DYNAMODB_SERVICE_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                operation: 'get_certifications'
            })
        });

        const data = await response.json();
        
        if (!response.ok) {
            throw new Error(data.error || 'Failed to fetch certifications');
        }

        return data.certifications || [];
    } catch (error) {
        console.error('Error fetching certifications:', error);
        throw error;
    }
};

/**
 * Add a new certification
 */
export const addCertification = async (certData) => {
    try {
        const response = await fetch(DYNAMODB_SERVICE_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                operation: 'add_certification',
                ...certData
            })
        });

        const data = await response.json();
        
        if (!response.ok) {
            throw new Error(data.error || 'Failed to add certification');
        }

        return data;
    } catch (error) {
        console.error('Error adding certification:', error);
        throw error;
    }
};

/**
 * Update a certification
 */
export const updateCertification = async (certId, certData) => {
    try {
        const response = await fetch(DYNAMODB_SERVICE_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                operation: 'update_certification',
                id: certId,
                ...certData
            })
        });

        const data = await response.json();
        
        if (!response.ok) {
            throw new Error(data.error || 'Failed to update certification');
        }

        return data;
    } catch (error) {
        console.error('Error updating certification:', error);
        throw error;
    }
};

/**
 * Delete a certification
 */
export const deleteCertification = async (certId) => {
    try {
        const response = await fetch(DYNAMODB_SERVICE_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                operation: 'delete_certification',
                id: certId
            })
        });

        const data = await response.json();
        
        if (!response.ok) {
            throw new Error(data.error || 'Failed to delete certification');
        }

        return data;
    } catch (error) {
        console.error('Error deleting certification:', error);
        throw error;
    }
};

// ==================== IMAGE UPLOADS ====================

/**
 * Get a pre-signed S3 upload URL
 */
export const getUploadUrl = async (filename, contentType, folder = 'images') => {
    try {
        const response = await fetch(DYNAMODB_SERVICE_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                operation: 'get_upload_url',
                filename,
                contentType,
                folder
            })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Failed to get upload URL');
        return data;
    } catch (error) {
        console.error('Error getting upload URL:', error);
        throw error;
    }
};

/**
 * Upload a file to S3 using pre-signed URL
 * Returns the public file URL
 */
export const uploadImageToS3 = async (file, folder = 'images') => {
    // Step 1: Get pre-signed URL
    const { uploadUrl, fileUrl } = await getUploadUrl(file.name, file.type, folder);

    // Step 2: Upload directly to S3
    const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file
    });

    if (!uploadResponse.ok) {
        throw new Error('Failed to upload image to S3');
    }

    return fileUrl;
};

// ==================== BADGES ====================

/**
 * Get all badges
 */
export const getBadges = async () => {
    try {
        const response = await fetch(DYNAMODB_SERVICE_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ operation: 'get_badges' })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Failed to fetch badges');
        return data.badges || [];
    } catch (error) {
        console.error('Error fetching badges:', error);
        throw error;
    }
};

/**
 * Add a new badge
 */
export const addBadge = async (badgeData) => {
    try {
        const response = await fetch(DYNAMODB_SERVICE_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ operation: 'add_badge', ...badgeData })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Failed to add badge');
        return data;
    } catch (error) {
        console.error('Error adding badge:', error);
        throw error;
    }
};

/**
 * Update a badge
 */
export const updateBadge = async (badgeId, badgeData) => {
    try {
        const response = await fetch(DYNAMODB_SERVICE_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ operation: 'update_badge', id: badgeId, ...badgeData })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Failed to update badge');
        return data;
    } catch (error) {
        console.error('Error updating badge:', error);
        throw error;
    }
};

/**
 * Delete a badge
 */
export const deleteBadge = async (badgeId) => {
    try {
        const response = await fetch(DYNAMODB_SERVICE_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ operation: 'delete_badge', id: badgeId })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Failed to delete badge');
        return data;
    } catch (error) {
        console.error('Error deleting badge:', error);
        throw error;
    }
};

// ==================== PRODUCTS ====================

/**
 * Get all products
 */
export const getProducts = async () => {
    try {
        const response = await fetch(DYNAMODB_SERVICE_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                operation: 'get_products'
            })
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || 'Failed to fetch products');
        }

        return data.products || [];
    } catch (error) {
        console.error('Error fetching products:', error);
        throw error;
    }
};

/**
 * Add a new product
 */
export const addProduct = async (productData) => {
    try {
        const response = await fetch(DYNAMODB_SERVICE_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                operation: 'add_product',
                ...productData
            })
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || 'Failed to add product');
        }

        return data;
    } catch (error) {
        console.error('Error adding product:', error);
        throw error;
    }
};

/**
 * Update a product
 */
export const updateProduct = async (productId, productData) => {
    try {
        const response = await fetch(DYNAMODB_SERVICE_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                operation: 'update_product',
                id: productId,
                ...productData
            })
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || 'Failed to update product');
        }

        return data;
    } catch (error) {
        console.error('Error updating product:', error);
        throw error;
    }
};

/**
 * Delete a product
 */
export const deleteProduct = async (productId) => {
    try {
        const response = await fetch(DYNAMODB_SERVICE_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                operation: 'delete_product',
                id: productId
            })
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || 'Failed to delete product');
        }

        return data;
    } catch (error) {
        console.error('Error deleting product:', error);
        throw error;
    }
};

// ==================== BLOGS ====================

/**
 * Get all blogs
 */
export const getBlogs = async () => {
    try {
        const response = await fetch(DYNAMODB_SERVICE_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                operation: 'get_blogs'
            })
        });

        const data = await response.json();
        
        if (!response.ok) {
            throw new Error(data.error || 'Failed to fetch blogs');
        }

        return data.blogs || [];
    } catch (error) {
        console.error('Error fetching blogs:', error);
        throw error;
    }
};

/**
 * Add a new blog
 */
export const addBlog = async (blogData) => {
    try {
        const response = await fetch(DYNAMODB_SERVICE_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                operation: 'add_blog',
                ...blogData
            })
        });

        const data = await response.json();
        
        if (!response.ok) {
            throw new Error(data.error || 'Failed to add blog');
        }

        return data;
    } catch (error) {
        console.error('Error adding blog:', error);
        throw error;
    }
};

/**
 * Update a blog
 */
export const updateBlog = async (blogId, blogData) => {
    try {
        const response = await fetch(DYNAMODB_SERVICE_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                operation: 'update_blog',
                id: blogId,
                ...blogData
            })
        });

        const data = await response.json();
        
        if (!response.ok) {
            throw new Error(data.error || 'Failed to update blog');
        }

        return data;
    } catch (error) {
        console.error('Error updating blog:', error);
        throw error;
    }
};

/**
 * Delete a blog
 */
export const deleteBlog = async (blogId) => {
    try {
        const response = await fetch(DYNAMODB_SERVICE_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                operation: 'delete_blog',
                id: blogId
            })
        });

        const data = await response.json();
        
        if (!response.ok) {
            throw new Error(data.error || 'Failed to delete blog');
        }

        return data;
    } catch (error) {
        console.error('Error deleting blog:', error);
        throw error;
    }
};
